import { persistWorkspaceId } from '@/lib/tenant/workspace-id-storage';
import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

interface Workspace {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
  /** Its own logo, else its company's; and the company's (GET /workspaces/my; useWorkspaceLogo). */
  logoUrl?: string | null;
  companyLogoUrl?: string | null;
  /** What stands in for those on a dark surface (useWorkspaceDarkLogo); null = they keep their plate. */
  darkLogoUrl?: string | null;
  companyDarkLogoUrl?: string | null;
}

interface WorkspaceState {
  activeWorkspaceId: string | null;
  activeCompanyId: string | null;
  activeDomainId: string | null;
  workspaces: Workspace[];
  /**
   * The last list had more than one workspace. Persisted, unlike the list, so a reload knows whether
   * the sidebar's switcher will be there and can hold its place while the list loads.
   */
  hadSeveralWorkspaces: boolean;
  setActiveWorkspace: (workspaceId: string) => void;
  setWorkspaces: (workspaces: Workspace[]) => void;
  getActiveWorkspace: () => Workspace | undefined;
  setActiveDomainId: (domainId: string | null) => void;
  // Progressive complexity helpers
  showWorkspaceSelector: () => boolean;
  // All-workspaces (company-level) view
  isAllWorkspacesView: () => boolean;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  devtools(
    persist(
      (set, get) => ({
        activeWorkspaceId: null,
        activeCompanyId: null,
        activeDomainId: null,
        workspaces: [],
        hadSeveralWorkspaces: false,
        setActiveWorkspace: (workspaceId: string) => {
          const workspace = get().workspaces.find(w => w.id === workspaceId);
          set({
            activeWorkspaceId: workspaceId,
            // For 'all' view, keep existing companyId (from first workspace)
            activeCompanyId: workspaceId === 'all'
              ? (get().activeCompanyId || get().workspaces[0]?.companyId || null)
              : (workspace?.companyId || null),
            // Reset domain when workspace changes
            activeDomainId: null,
          });
          // Sync for BOTH the browser API client and the server-side route
          // handlers (skip for the 'all' view). See persistWorkspaceId — the
          // cookie half is what the server reads, and the switcher never wrote
          // it, so server-side calls stayed on the login-time workspace.
          if (workspaceId !== 'all') persistWorkspaceId(workspaceId);
        },
        setWorkspaces: (workspaces: Workspace[]) => {
          set({ workspaces, hadSeveralWorkspaces: workspaces.length > 1 });
          if (workspaces.length === 0) return;

          const active = get().activeWorkspaceId;

          // Auto-select first workspace if none active
          if (!active) {
            get().setActiveWorkspace(workspaces[0].id);
            return;
          }

          // RECONCILE A STALE ACTIVE WORKSPACE. `/workspaces/my` filters
          // `isNull(deletedAt)`, so an id that is no longer in the list has
          // been archived. Left alone it stays in localStorage, `client.ts`
          // ships it as `x-workspace-id`, `TenantContextGuard` resolves it to
          // null and throws `ForbiddenException('Invalid tenant context')` on
          // EVERY request — with no in-app way back short of clearing browser
          // storage. `'all'` is the company-wide VIEW sentinel, not a
          // workspace, so it is never in the list and must not be reconciled,
          // unless one workspace is left: that view is offered by the sidebar's
          // switcher, and one workspace has no switcher to leave it with.
          if (active === 'all') {
            if (workspaces.length === 1) get().setActiveWorkspace(workspaces[0].id);
            return;
          }
          if (!workspaces.some((w) => w.id === active)) {
            get().setActiveWorkspace(workspaces[0].id);
          }
        },
        getActiveWorkspace: () => {
          const state = get();
          return state.workspaces.find(w => w.id === state.activeWorkspaceId);
        },
        setActiveDomainId: (domainId: string | null) => {
          set({ activeDomainId: domainId });
        },
        showWorkspaceSelector: () => get().workspaces.length > 1,
        isAllWorkspacesView: () => get().activeWorkspaceId === 'all',
      }),
      {
        name: 'hf-app:workspace',
        partialize: (state) => ({
          activeWorkspaceId: state.activeWorkspaceId,
          activeCompanyId: state.activeCompanyId,
          hadSeveralWorkspaces: state.hadSeveralWorkspaces,
        }),
      },
    ),
    { name: 'WorkspaceStore' },
  ),
);
